import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import axios from "axios";
import { MuiMaterials } from "../@index.js";
import { useAuth } from "../provider/authProvider.js";
import * as global from "../data/global.js";
import { saveSelectedSite } from "../data/global.js";
import CarbonDioxideChart from "./CarbonDioxideChart";
import CarbonDioxideSettings from "./CarbonDioxideSettings";
import CarbonDioxideChartFilter from "./CarbonDioxideChartFilter";
import CarbonDioxideUpload from "./CarbonDioxideUpload.js";
import {
    WindowChrome,
    ModalBody,
    ModalContentTile,
    SiteSelectPhase,
    Spinner,
    SITE_WINDOW_IDS,
    getWindowChromeThemeColor,
    ensureSiteInfosLoaded,
} from "./windowChrome/index.js";

const getThemeColor = getWindowChromeThemeColor;

const CarbonDioxideWindow = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const { clearToken } = useAuth();
    const autoLoadHandledRef = useRef(false);

    const [forceUpdate, setForceUpdate] = useState(0);
    const [phase, setPhase] = useState("select");
    const [sites, setSites] = useState([]);
    const [sitesLoading, setSitesLoading] = useState(true);
    const carriedSiteGuid =
        (location.state?.autoLoad && location.state?.siteGuid) || location.state?.siteGuid || "";
    const [pickerSiteGuid, setPickerSiteGuid] = useState(() => carriedSiteGuid || "");
    const [loadedSiteGuid, setLoadedSiteGuid] = useState("");
    const [loading, setLoading] = useState(() => Boolean(carriedSiteGuid && location.state?.autoLoad));

    const [updateToggle, setUpdateToggle] = useState(false);
    const [chartFilterOptions, setChartFilterOptions] = useState({
        binId: global.gw.selectedSite.carbonDioxideBinId ? global.gw.selectedSite.carbonDioxideBinId : 0,
        binName: global.gw.selectedSite.carbonDioxideBinName ? global.gw.selectedSite.carbonDioxideBinName : "All",
        deviceId: null,
        dateFrom: new Date(),
        dateTo: new Date(),
        hourFrom: 0,
        hourTo: 24,
    });

    useEffect(() => {
        const onTheme = () => setForceUpdate((n) => n + 1);
        window.addEventListener("themeChanged", onTheme);
        return () => window.removeEventListener("themeChanged", onTheme);
    }, []);

    useEffect(() => {
        let cancelled = false;
        const apply = () => {
            const all = [...(global.gw.siteInfos || [])].filter((s) => s.siteGuid && !s.isHms);
            all.sort((a, b) =>
                (a.siteName || "").toLowerCase().localeCompare((b.siteName || "").toLowerCase()),
            );
            if (cancelled) return;
            setSites(all);
            setSitesLoading(false);
        };
        if ((global.gw.siteInfos || []).length > 0) {
            apply();
            return undefined;
        }
        setSitesLoading(true);
        ensureSiteInfosLoaded().then(() => {
            if (!cancelled) apply();
        });
        return () => {
            cancelled = true;
        };
    }, [forceUpdate]);

    useLayoutEffect(() => {
        if (!location.state?.autoLoad || !location.state?.siteGuid) return;
        setPickerSiteGuid(location.state.siteGuid);
    }, [location.state?.autoLoad, location.state?.siteGuid]);

    const redirectToLogin = useCallback(() => {
        clearToken();
        delete axios.defaults.headers.common.Authorization;
        global.gw.token = null;
        global.gw.auth = null;
        global.gw.isAuthenticated = false;
        setTimeout(() => {
            window.location.href = "/emailLogin";
        }, 100);
    }, [clearToken]);

    const applySiteSelection = useCallback(async (siteGuid) => {
        if (!siteGuid) return;
        setLoading(true);
        try {
            const want = global.normalizeGuidForCompare
                ? global.normalizeGuidForCompare(siteGuid)
                : String(siteGuid || "").toLowerCase();
            const info = (global.gw.siteInfos || []).find((s) => {
                const g = s?.siteGuid;
                if (!g) return false;
                const have = global.normalizeGuidForCompare
                    ? global.normalizeGuidForCompare(g)
                    : String(g).toLowerCase();
                return have === want;
            });

            if (info) {
                if (global.gw.selectedSite?.refreshId) {
                    clearTimeout(global.gw.selectedSite.refreshId);
                }
                if (
                    !global.gw.selectedSite
                    || global.normalizeGuidForCompare?.(global.gw.selectedSite.siteGuid)
                        !== global.normalizeGuidForCompare?.(info.siteGuid)
                ) {
                    global.gw.selectedSite = new global.Site();
                }
                global.gw.selectedSite.siteIndex = info.siteIndex;
                global.gw.selectedSite.siteGuid = info.siteGuid;
                global.gw.selectedSite.siteName = info.siteName;
                global.gw.selectedSite.lastScan = info.lastScan;
                saveSelectedSite?.();
            } else if (global.gw.selectedSite) {
                global.gw.selectedSite.siteGuid = siteGuid;
            }

            await global.initializeCustomerSite(navigate);

            let retryCount = 0;
            const maxRetries = 30;
            while (retryCount < maxRetries) {
                if (global.gw.selectedSite.isSiteInitialized) break;
                await new Promise((resolve) => setTimeout(resolve, 100));
                retryCount++;
            }

            setChartFilterOptions((prev) => ({
                ...prev,
                binId: global.gw.selectedSite.carbonDioxideBinId || 0,
                binName: global.gw.selectedSite.carbonDioxideBinName || "All",
            }));

            setLoadedSiteGuid(info?.siteGuid || siteGuid);
            setPickerSiteGuid(info?.siteGuid || siteGuid);
            setPhase("view");
        } catch (error) {
            console.error("CarbonDioxideWindow: failed to load site", error);
            const is401 = (error.response && error.response.status === 401)
                || (error.message && (error.message.includes("401") || error.message.includes("Unauthorized")));
            if (is401 && global.gw.token && global.gw.auth) {
                redirectToLogin();
                return;
            }
            setPhase("select");
            setLoadedSiteGuid("");
        } finally {
            setLoading(false);
        }
    }, [navigate, redirectToLogin]);

    useEffect(() => {
        const siteGuid = location.state?.siteGuid;
        if (!siteGuid || !location.state?.autoLoad) return;
        if (autoLoadHandledRef.current === siteGuid) return;
        autoLoadHandledRef.current = siteGuid;
        setPickerSiteGuid(siteGuid);
        applySiteSelection(siteGuid);
    }, [location.state, applySiteSelection]);

    const handleContinue = useCallback((guid) => {
        const next = guid || pickerSiteGuid;
        if (!next) return;
        setPickerSiteGuid(next);
        applySiteSelection(next);
    }, [pickerSiteGuid, applySiteSelection]);

    const handleHeaderSiteChange = useCallback((siteGuid) => {
        if (!siteGuid) return;
        setPickerSiteGuid(siteGuid);
        applySiteSelection(siteGuid);
    }, [applySiteSelection]);

    const handleClose = () => {
        navigate("/sites", { replace: true });
    };

    const activeSiteGuid = loadedSiteGuid || pickerSiteGuid || "";

    const loadingOverlay = loading ? (
        <MuiMaterials.Box
            sx={{
                position: "absolute",
                inset: 0,
                zIndex: 2,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                backgroundColor: getThemeColor("rgba(232,232,232,0.7)", "rgba(26,26,26,0.7)"),
            }}
        >
            <Spinner size="modal" />
        </MuiMaterials.Box>
    ) : null;

    return (
        <WindowChrome
            title="CO₂"
            toolTitle="CO₂"
            siteWindowId={SITE_WINDOW_IDS.co2}
            siteGuid={activeSiteGuid || null}
            showHeaderSiteSelect={phase === "view"}
            onHeaderSiteChange={handleHeaderSiteChange}
            headerSites={sites}
            headerFilterSite={(s) => !s.isHms}
            headerSiteSelectDisabled={loading || sitesLoading}
            actions={
                phase === "view" && !loading ? (
                    <>
                        <CarbonDioxideUpload />
                        <CarbonDioxideSettings
                            updateToggle={updateToggle}
                            setUpdateToggle={setUpdateToggle}
                        />
                        <CarbonDioxideChartFilter
                            chartFilterOptions={chartFilterOptions}
                            setChartFilterOptions={setChartFilterOptions}
                        />
                    </>
                ) : null
            }
            onClose={handleClose}
            loadingOverlay={loadingOverlay}
            className="carbonDioxideWindow"
        >
            {phase === "select" ? (
                <SiteSelectPhase
                    value={pickerSiteGuid}
                    onChange={setPickerSiteGuid}
                    onContinue={handleContinue}
                    onCancel={handleClose}
                    loading={sitesLoading || loading}
                    filterSite={(s) => !s.isHms}
                />
            ) : null}

            {phase === "view" && !loading ? (
                <ModalBody className="hide-scrollbar">
                    <ModalContentTile
                        className="hide-scrollbar"
                        sx={{
                            flex: 1,
                            minHeight: 0,
                            display: "flex",
                            flexDirection: "column",
                            overflow: "hidden",
                            p: "12px 16px",
                        }}
                    >
                        <CarbonDioxideChart
                            key={`co2-chart-${activeSiteGuid}`}
                            chartFilterOptions={chartFilterOptions}
                            settingsUpdateToggle={updateToggle}
                        />
                    </ModalContentTile>
                </ModalBody>
            ) : null}
        </WindowChrome>
    );
};

export default CarbonDioxideWindow;
